import type { FC } from "react";
import { useMemo } from "react";

import { default as styled } from "styled-components";

import { ShareBarCell } from "@similarweb/pro-logic-components";
import { colorsPalettes, rgba } from "@similarweb/styles";
import { EmptyState, MiniFlexTable } from "@similarweb/ui-components";

import { useSelectorTrigger } from "app/components/filters-bar/useSelectorTrigger";
import { TrafficShare } from "app/components/React/Table/cells";
import { ChangeStateCell } from "app/components/React/Table/cells/ChangeStateCell";
import { DefaultCellHeader, DefaultCellHeaderRightAlign } from "app/components/React/Table/headerCells";
import { FooterComponent } from "app/components/widget/WidgetFooter";
import { WidgetLoader } from "app/components/widget-loader/WidgetLoader";
import { WidgetTitle } from "app/components/widget-title/WidgetTitle";
import { useSubtitleFilters } from "app/custom-hooks/useSubtitleFilters";
import { NO_DATA_SIGN } from "app/filters/const";
import { translate } from "app/filters/i18nFilter";
import { formatShare } from "app/filters/ngFilters";
import { useTableRows } from "app/pages/digital-marketing/ad-intelligence/common/advertisersPublishers/hooks/useTableRows";
import { useWebSourceSubtitleFilter } from "app/pages/digital-marketing/ad-intelligence/common/hooks/useWebSourceSubtitleFilter";
import type { ISortProps } from "app/pages/digital-marketing/ad-intelligence/common/utils/utils";
import { getChangeCellValues, NA_VALUE } from "app/pages/digital-marketing/ad-intelligence/common/utils/utils";
import { allTrackers } from "app/services/track/track";
import { devicesTypes } from "app/UtilitiesAndConstants/Constants/DevicesTypes";
import { CoreWebsiteCell } from "pro-features/components/core cells/src/CoreWebsiteCell/CoreWebsiteCell";
import { CenteredFlexRow, FlexRow, RightFlexRow } from "pro-features/styled components/StyledFlex/src/StyledFlex";
import { proNavigator } from "single-spa/react-app/ProNavigator";

const ContentWrapper = styled(CenteredFlexRow)`
    height: 284px;
`;

const HeaderStyle = styled(FlexRow)`
    justify-content: space-between;
    box-sizing: border-box;
    padding: 24px;
`;

const CtaWrapper = styled(RightFlexRow)`
    height: 52px;
    border-top: 1px solid ${colorsPalettes.carbon[100]};

    button {
        margin: 5px 8px;
    }
`;

const ChangeCellContainer = styled(RightFlexRow)`
    height: 100%;
`;

const TopPublishersWrapper = styled.div<{ isCompare?: boolean }>`
    background-color: ${colorsPalettes.carbon[0]};
    height: 384px;
    box-shadow: 0 3px 6px ${rgba(colorsPalettes.carbon[500], 0.08)};
    border-radius: 4px;
    ${({ isCompare = false }) => isCompare && `margin-bottom:24px;`};
`;

const StyledTable = styled(MiniFlexTable)`
    &.MiniFlexTable {
        padding-bottom: 0;
        height: 232px;
        flex: 1;
    }

    .MiniFlexTable-cell {
        height: 40px;
        vertical-align: middle;
        font-size: 14px;

        &:not(:first-child) {
            border-top: 1px solid ${colorsPalettes.carbon[50]};
        }
    }

    .MiniFlexTable-column {
        &:first-of-type {
            .MiniFlexTable-headerCell,
            .MiniFlexTable-cell {
                padding-left: 24px;
            }
        }

        &:last-of-type {
            .MiniFlexTable-headerCell,
            .MiniFlexTable-cell {
                padding-right: 24px;
            }
        }

        &:nth-of-type(2) {
            .MiniFlexTable-cell {
                > div {
                    height: 100%;
                    align-items: center;
                    display: flex;
                }
            }
        }
    }

    .MiniFlexTable-headerCell {
        margin-bottom: 2px;
        font-weight: 500;
        font-size: 12px;
        color: ${rgba(colorsPalettes.carbon[500], 0.6)};
    }
`;

interface IWWOTopPublishersTableProps {
    getCtaLink: (withCompare?: boolean) => { href: string; target: string };
    unlockHook?: any;
    hideFooter?: boolean;
}

export const WWOAdIntelTopPublishersTable: FC<IWWOTopPublishersTableProps> = ({
    getCtaLink,
    unlockHook = null,
    hideFooter = false,
}) => {
    const params = proNavigator.getParams() as { key?: string; country?: string };
    const isCompare = (params.key?.split(",").length ?? 0) > 1;
    const subtitleFilters = useSubtitleFilters("date", "country");
    const webSourceSubtitleFilter = useWebSourceSubtitleFilter(devicesTypes.TOTAL);
    const combinedSubtitleFilters = [...subtitleFilters, webSourceSubtitleFilter];
    const { selectorHandlers } = useSelectorTrigger("wwo-top-publishers");

    const title = translate("wa.wwo.toppublishers.title");
    const tooltip = translate("wa.wwo.toppublishers.title.tooltip");

    const { data, isLoading } = useTableRows({
        controller: "advertiser",
        resource: "publisher",
        page: 1,
        pageSize: 5,
        isCompareMode: isCompare,
        sort: { sort: "visits", asc: false } as ISortProps,
        withChildren: false,
    });

    const ctaLink = getCtaLink();

    const columns = useMemo(() => {
        if (isCompare) {
            return [
                {
                    field: "publisher",
                    displayName: translate("wa.wwo.toppublishers.domain"),
                    headerComponent: DefaultCellHeader,
                    cellComponent: ({ value }) => {
                        const props = {
                            displayName: "CoreWebsiteCell",
                            domain: value?.url,
                            icon: value?.favicon,
                            hideTrackButton: true,
                            showToolTip: false,
                        };
                        return value?.url ? <CoreWebsiteCell {...props} /> : null;
                    },
                },
                {
                    field: "visits",
                    displayName: translate("wa.wwo.toppublishers.share.split"),
                    headerComponent: DefaultCellHeader,
                    cellComponent: ({ value }) => {
                        const siteList = params.key.split(",");
                        const data = siteList.map((site) => value?.value?.[site]);
                        return (
                            <div>
                                {data.length > 0 ? (
                                    <ShareBarCell
                                        values={data}
                                        tooltip={{
                                            title: translate("wa.wwo.toppublishers.share.tooltip"),
                                            labels: siteList,
                                        }}
                                        valueFormatter={(val) => formatShare(val, 1, 0.01, NA_VALUE) as string}
                                    />
                                ) : (
                                    NA_VALUE
                                )}
                            </div>
                        );
                    },
                },
            ];
        }

        return [
            {
                field: "publisher",
                displayName: translate("wa.wwo.toppublishers.domain"),
                headerComponent: DefaultCellHeader,
                cellComponent: ({ value }) => {
                    const props = {
                        displayName: "CoreWebsiteCell",
                        domain: value.url,
                        icon: value.favicon,
                        hideTrackButton: true,
                        showToolTip: false,
                    };
                    return <CoreWebsiteCell {...props} />;
                },
            },
            {
                field: "visits",
                displayName: translate("wa.wwo.toppublishers.share"),
                headerComponent: DefaultCellHeader,
                cellComponent: ({ value }) => {
                    return (
                        <TrafficShare
                            value={value.value}
                            row={{}}
                            undefinedSign={NO_DATA_SIGN}
                        />
                    );
                },
            },
            {
                field: "visitsChange",
                displayName: translate("wa.wwo.toppublishers.change"),
                cellComponent: ({ value: { value, isNew } }) => {
                    const { changeState, precision } = getChangeCellValues({ value, isNew });
                    return (
                        <ChangeCellContainer>
                            <ChangeStateCell
                                value={{
                                    change: value,
                                    changeState,
                                }}
                                undefinedSign={"-"}
                                isPercentage={true}
                                cellFilter={`simplePercentage:${precision}`}
                            />
                        </ChangeCellContainer>
                    );
                },
                headerComponent: DefaultCellHeaderRightAlign,
                minWidth: 85,
                visible: true,
            },
        ];
    }, [isCompare, params.key]);

    const ctaClicked = () => {
        allTrackers.dispatchTrackEvent(
            "Internal Link",
            "click",
            "Display Advertising/Top Publishers/See more publishers",
        );
    };

    return (
        <TopPublishersWrapper
            isCompare={isCompare}
            data-automation="wwo-top-publishers"
        >
            <HeaderStyle>
                <WidgetTitle
                    title={title}
                    tooltip={tooltip}
                    filters={combinedSubtitleFilters}
                    {...selectorHandlers}
                />
            </HeaderStyle>
            {isLoading ? (
                <ContentWrapper>
                    <WidgetLoader />
                </ContentWrapper>
            ) : data?.items?.length ? (
                <>
                    <StyledTable
                        className="MiniFlexTable"
                        data={data.items}
                        columns={columns}
                    />
                    {hideFooter ? null : (
                        <CtaWrapper>
                            <FooterComponent
                                href={ctaLink.href}
                                onClick={ctaClicked}
                                text="wwo.top.publishers.button"
                                unlockHook={unlockHook}
                                target={ctaLink.target}
                            />
                        </CtaWrapper>
                    )}
                </>
            ) : (
                <ContentWrapper>
                    <EmptyState
                        title={translate("wa.wwo.no.data.title")}
                        subtitle={translate("wa.wwo.no.data.sub.title")}
                    />
                </ContentWrapper>
            )}
        </TopPublishersWrapper>
    );
};
